import Logo from "@/assets/images/brt-logo.png"
import UserAvatar from "@/library/Avatar"
import HeaderLogo from "@/library/HeaderLogo"
import BRTThemeProvider from "@/providers/theme"
import { Close as CloseIcon, Menu as MenuIcon } from "@mui/icons-material"
import { AppBar, Box, Container, Divider, Drawer, IconButton, Toolbar, useMediaQuery, useTheme } from "@mui/material"
import PropTypes from "prop-types"
import React, { useCallback, useMemo } from "react"
import { getAuthorizedNavItems } from "../../navbar_links"
import NavigationItems from "./NavigationItems"
import UserMenu from "./UserMenu"

/**
 * Renders the application header with navigation, logo, and user menu.
 * Adapts layout for mobile and desktop, providing a responsive navigation drawer on mobile.
 * Displays navigation items based on the current user's role, permissions, and feature flags.
 * Integrates theme provider and handles user menu interactions.
 *
 * @param {Object} props
 * @param {Object} props.currentUser - The current user object with id, name, role, permissions, feature_flags, and parent.
 * @returns {JSX.Element} The header component.
 */
const Header = ({ currentUser }) => {
  const theme = useTheme()
  const [anchorElUser, setAnchorElUser] = React.useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const handleOpenUserMenu = useCallback(event => {
    setAnchorElUser(event.currentTarget)
  }, [])

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null)
  }, [])

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen(prev => !prev)
  }, [])

  const handleMobileNavClick = useCallback(() => {
    setMobileMenuOpen(false)
  }, [])

  const navItems = useMemo(
    () => getAuthorizedNavItems(currentUser?.role, currentUser?.permissions, currentUser?.feature_flags),
    [currentUser?.role, currentUser?.permissions, currentUser?.feature_flags]
  )

  return (
    <BRTThemeProvider>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        data-testid="header"
        sx={{
          marginBottom: 2,
          boxShadow: 0,
          py: 2,
        }}
      >
        <Container maxWidth={false}>
          <Toolbar
            disableGutters={isMobile}
            data-testid="header-toolbar"
            sx={{
              minHeight: { xs: 56, sm: 64, md: 72 },
              px: { md: 0, lg: 6 },
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            {isMobile && (
              <IconButton
                onClick={handleMobileMenuToggle}
                size="large"
                edge="start"
                color="inherit"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                sx={{ mr: 1 }}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            )}

            <HeaderLogo />

            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
                <NavigationItems navItems={navItems} />
              </Box>
            )}

            <UserAvatar currentUser={currentUser} onClick={handleOpenUserMenu} isMenuOpen={Boolean(anchorElUser)} />
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        data-testid="mobile-menu"
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 320 },
            p: 0,
            pt: 2,
          },
        }}
      >
        <Box sx={{ px: 2, pb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box
            component="img"
            src={Logo}
            alt="Braintrust Logo"
            data-testid="mobile-menu-logo"
            sx={{
              width: "130px",
              height: "auto",
              display: "block",
            }}
          />
          <IconButton
            onClick={handleMobileMenuToggle}
            size="large"
            edge="end"
            color="inherit"
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ my: 2 }} />
        <NavigationItems navItems={navItems} isMobile onNavClick={handleMobileNavClick} />
      </Drawer>

      <UserMenu anchorEl={anchorElUser} onClose={handleCloseUserMenu} currentUser={currentUser} />
    </BRTThemeProvider>
  )
}

Header.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.string,
    permissions: PropTypes.object,
    feature_flags: PropTypes.object,
    parent: PropTypes.bool,
  }).isRequired,
}

export default React.memo(Header)
